// Primitivas de UI compartidas por las pestanas de Reportes Inteligentes.
// Sin logica de negocio: reciben datos ya calculados y los dibujan.

import React from 'react';
import { ArrowDown, ArrowUp, Sparkles } from 'lucide-react';

export function KpiCard({ icon: Icon, label, value, delta, accent, hint, inverted = false }) {
  const showDelta = typeof delta === 'number' && Number.isFinite(delta);
  const positive = inverted ? delta < 0 : delta > 0;
  const negative = inverted ? delta > 0 : delta < 0;
  const deltaColor = !showDelta
    ? 'text-slate-400'
    : Math.abs(delta) < 0.1
    ? 'text-slate-400'
    : positive
    ? 'text-emerald-600'
    : negative
    ? 'text-rose-600'
    : 'text-slate-500';
  const DeltaIcon = !showDelta || Math.abs(delta) < 0.1 ? null : delta > 0 ? ArrowUp : ArrowDown;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div className={`grid h-10 w-10 place-items-center rounded-xl ${accent}`}>
          <Icon size={18} />
        </div>
        {showDelta && (
          <div className={`flex items-center gap-1 text-[10px] font-black uppercase tracking-widest ${deltaColor}`}>
            {DeltaIcon && <DeltaIcon size={12} />}
            {Math.abs(delta).toLocaleString('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%
          </div>
        )}
      </div>
      <p className="mt-3 text-[10px] font-black uppercase tracking-widest text-slate-400">{label}</p>
      <p className="mt-1 text-2xl font-black text-slate-900 tracking-tighter">{value}</p>
      {hint && <p className="mt-1 text-[10px] font-bold text-slate-400">{hint}</p>}
    </div>
  );
}

export function AlertCard({ icon: Icon, title, count, detail, tone = 'amber', onClick }) {
  const toneMap = {
    amber: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: 'bg-amber-100 text-amber-700' },
    rose: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', icon: 'bg-rose-100 text-rose-700' },
    sky: { bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200', icon: 'bg-sky-100 text-sky-700' },
    slate: { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200', icon: 'bg-slate-100 text-slate-600' },
  };
  const t = toneMap[tone] || toneMap.amber;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-2xl border ${t.border} ${t.bg} p-4 transition-all hover:shadow-md ${
        onClick ? 'cursor-pointer' : 'cursor-default'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${t.icon}`}>
          <Icon size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <p className={`text-[10px] font-black uppercase tracking-widest ${t.text}`}>{title}</p>
          <p className="mt-1 text-2xl font-black text-slate-900 tracking-tighter">{count}</p>
          {detail && <p className="mt-1 text-[10px] font-medium text-slate-500 line-clamp-2">{detail}</p>}
        </div>
        {onClick && <ArrowRight size={14} className="text-slate-400 mt-1" />}
      </div>
    </button>
  );
}

export function SectionCard({ title, icon: Icon, action, children, className = '' }) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden ${className}`}>
      <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={16} className="text-slate-400" />}
          <h2 className="text-[11px] font-black uppercase tracking-widest text-slate-700">{title}</h2>
        </div>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

export function EmptyState({ icon: Icon = Sparkles, message }) {
  return (
    <div className="py-10 flex flex-col items-center gap-2 text-slate-400">
      <div className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center">
        <Icon size={20} />
      </div>
      <p className="text-[10px] font-black uppercase tracking-widest">{message || 'Sin datos en el periodo'}</p>
    </div>
  );
}

// Tabla genérica para los reportes de productos. `columns` = [{ key, label,
// align, render?(row), cellClass?(row) }]. Mantiene el mismo theming que el
// resto del módulo (header sticky, divide-y, etc.).
export function DataTable({ columns, rows, footer, maxHeight = 420, emptyMessage }) {
  if (!rows.length) return <EmptyState message={emptyMessage} />;
  return (
    <div className="overflow-x-auto" style={{ maxHeight, overflowY: 'auto' }}>
      <table className="w-full text-left text-xs">
        <thead className="sticky top-0 bg-white z-10">
          <tr className="text-[9px] font-black uppercase tracking-widest text-slate-400 border-b border-slate-100">
            {columns.map((c) => (
              <th key={c.key} className={`pb-2 ${c.align === 'right' ? 'text-right' : ''}`}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row, i) => (
            <tr key={row._key ?? row.id ?? i}>
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={`py-2 ${c.align === 'right' ? 'text-right' : ''} ${
                    typeof c.cellClass === 'function' ? c.cellClass(row) : c.cellClass || 'text-slate-700'
                  }`}
                >
                  {c.render ? c.render(row) : row[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer}
      </table>
    </div>
  );
}
